import type {
  IGetRecordResponse,
  IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  resolveSequentially,
  HISTORY_DATE_FORMAT,
  ISO_DATE_FORMAT,
  sortByCreatedAt,
  Timestamp,
  Timezone,
  toInt,
  toISODate,
  toMoment,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import {
  getDefaultTreatmentPlanName,
  IPatientTreatmentPlanJobData,
  IStepWithIdentifiers,
  ITreatmentPlanStepPair,
} from '../../../../destination/entities/patient-treatment-plans';
import { TranslationMapHandler } from '../../../../translation-map';
import {
  ExactAppointment,
  ExactAppointmentStatus,
} from '../../../source/entities/patient-appointment';
import {
  ExactTreatment,
  type IExactTreatment,
} from '../../../source/entities/patient-treatments';
import { compact, first, groupBy, sortBy, upperFirst } from 'lodash';
import {
  CalculateTreatmentWeight,
  ChartedItemScopeResolver,
  ChartedSurface,
  ChartedTreatment,
  determineTaxFromStrategy,
  FeeSchedule,
  PricedServiceCodeEntry,
  stafferToNamedDoc,
  TreatmentPlan,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import { resolveExactStaffer } from '../../mappings/staff';
import {
  AppointmentStatus,
  ChartableSurface,
  TreatmentPlanStatus,
  TreatmentStepStatus,
  type IChartedSurface,
  type IChartedTreatment,
  type IFeeSchedule,
  type IStaffer,
  type ITreatmentCategory,
} from '@principle-theorem/principle-core/interfaces';
import * as moment from 'moment-timezone';
import { resolveFeeSchedule } from '../../../../mappings/fee-schedules';
import { ExactStepIdentity } from './exact-step-identity';
import {
  ExactStepKind,
  planExactTreatmentSteps,
  type IExactAppointmentStepLink,
  type IExactStepBlueprint,
} from './exact-step-planner';
import { TaxStrategy, roundTo2Decimals } from '@principle-theorem/accounting';
import {
  determineToothSurfaceByScope,
  resolveMappedCodeTreatmentPair,
  resolveMappTreatmentConfiguration,
} from '../../../../mappings/item-codes';
import { getResolvedTreatmentData } from '../../../util/helpers';
import { getExactChartedRefs } from '../../../util/tooth';
import { ExactPatient } from '../../../source/entities/patient';

export interface IExactTreatmentPlanBuildResult {
  planPairs: ITreatmentPlanStepPair[];
  appointmentLinks: IExactAppointmentStepLink[];
}

export class ExactTreatmentPlanBuilder {
  static getPlanIdentifier(sourcePatientId: string, planId?: string): string {
    if (planId) {
      return `${sourcePatientId}-plan-${planId}`;
    }
    return `${sourcePatientId}-plan`;
  }

  static async buildTreatmentPlanData(
    data: IPatientTreatmentPlanJobData<ExactPatient>,
    translationMap: TranslationMapHandler,
    sourceTreatments: IGetRecordResponse<ExactTreatment>[],
    migration: WithRef<IPracticeMigration>,
    sourcePatientId: string,
    sourceAppointments: IGetRecordResponse<ExactAppointment>[]
  ): Promise<IExactTreatmentPlanBuildResult> {
    const planGroups = groupBy(sourceTreatments, (sourceTreatment) =>
      ExactStepIdentity.planKey(sourceTreatment.data.treatment_plan_id)
    );

    const defaultFeeSchedule = await FeeSchedule.getPreferredOrDefault(
      migration.configuration.organisation
    );

    const chartedTreatmentsById =
      await ExactTreatmentPlanBuilder._getChartedTreatmentsById(
        Object.values(planGroups),
        toNamedDocument(defaultFeeSchedule),
        data,
        translationMap,
        migration.configuration.timezone
      );
    const chartableTreatments = sourceTreatments.filter((treatment) =>
      chartedTreatmentsById.has(treatment.data.treatment_id)
    );
    const stepPlan = planExactTreatmentSteps(
      sourcePatientId,
      chartableTreatments,
      sourceAppointments,
      migration.configuration.timezone
    );

    const plans: ITreatmentPlanStepPair[] = [];

    await resolveSequentially(Object.values(planGroups), async (treatments) => {
      treatments = sortBy(treatments, (treatment) =>
        getVisitGroupKey(treatment, treatments)
      );

      const planId = treatments[0].data.treatment_plan_id ?? undefined;
      const planIdentifier = ExactTreatmentPlanBuilder.getPlanIdentifier(
        sourcePatientId,
        planId
      );

      const createdAt = treatments[0].record.translations.plannedDate
        ? toTimestamp(
            toMomentTz(
              treatments[0].record.translations.plannedDate,
              migration.configuration.timezone
            )
          )
        : undefined;

      const defaultPlanName = createdAt
        ? `Migrated Treatment Plan ${toISODate(createdAt)}`
        : getDefaultTreatmentPlanName(migration);

      const planName =
        treatments[0].data.treatment_description ?? defaultPlanName;

      const practitioner = await resolveExactStaffer(
        treatments[0].data.provider_code ?? '',
        translationMap,
        data.staff
      );

      let planSchedule: INamedDocument<IFeeSchedule> | undefined;
      if (treatments[0].data.payment_plan_id) {
        const scheduleResolve = resolveFeeSchedule(
          translationMap,
          toNamedDocument(defaultFeeSchedule)
        );
        planSchedule = await scheduleResolve(
          treatments[0].data.payment_plan_id
        );
      }

      const plan = {
        ...TreatmentPlan.init({
          name: planName.trim(),
          practitioner: practitioner
            ? stafferToNamedDoc(practitioner)
            : undefined,
          createdAt,
          feeSchedule: planSchedule,
        }),
        sourceIdentifier: planIdentifier,
      };

      const planKey = ExactStepIdentity.planKey(planId);
      const steps = await resolveSequentially(
        stepPlan.steps.filter((blueprint) => blueprint.planKey === planKey),
        (blueprint) =>
          ExactTreatmentPlanBuilder._buildStepFromBlueprint(
            blueprint,
            chartedTreatmentsById,
            data.treatmentCategories,
            migration.configuration.timezone
          )
      );

      if (!steps.length) {
        return;
      }

      const earliestStepDate = first(steps.sort(sortByCreatedAt))?.createdAt;
      if (earliestStepDate) {
        plan.createdAt = earliestStepDate;
      }

      const status = ExactTreatmentPlanBuilder.determinePlanStatus(
        steps,
        plan.createdAt
      );

      plans.push({
        plan: {
          ...plan,
          status,
        },
        steps,
      });
    });

    return {
      planPairs: plans,
      appointmentLinks: stepPlan.appointmentLinks,
    };
  }

  static determinePlanStatus(
    steps: IStepWithIdentifiers[],
    planCreatedAt: Timestamp
  ): TreatmentPlanStatus {
    const allComplete = steps.every(
      (step) => step.status === TreatmentStepStatus.Complete
    );
    if (allComplete && steps.length) {
      return TreatmentPlanStatus.Completed;
    }

    const planAgeInMonths = moment().diff(toMoment(planCreatedAt), 'months');
    const someComplete = steps.some(
      (step) => step.status === TreatmentStepStatus.Complete
    );
    if (someComplete) {
      return TreatmentPlanStatus.InProgress;
    }

    const allIncomplete = steps.every(
      (step) => step.status === TreatmentStepStatus.Incomplete
    );
    if (allIncomplete && planAgeInMonths > 12) {
      return TreatmentPlanStatus.Declined;
    }

    return TreatmentPlanStatus.Draft;
  }

  static async getChartedTreatments(
    sourceTreatments: IExactTreatment[],
    getPreferredOrDefaultRef: (
      feeSheduleId?: string
    ) => Promise<INamedDocument<IFeeSchedule>>,
    data: IPatientTreatmentPlanJobData<ExactPatient>,
    translationMap: TranslationMapHandler,
    timezone: Timezone
  ): Promise<IChartedTreatment[]> {
    const treatments: IChartedTreatment[] = [];
    await resolveSequentially(sourceTreatments, async (sourceTreatment) => {
      const practitioner = await resolveExactStaffer(
        sourceTreatment.provider_code ?? '',
        translationMap,
        data.staff
      );
      if (!practitioner) {
        throw new Error(
          `Couldn't resolve practitioner for treatment ${sourceTreatment.provider_code}`
        );
      }

      const [code, mappedTreatment] = resolveMappedCodeTreatmentPair(
        data.sourceItemCodes,
        data.treatmentConfigurationMappings,
        sourceTreatment.service_code,
        sourceTreatment.service_code
      );
      if (!code && !mappedTreatment) {
        // eslint-disable-next-line no-console
        console.log(
          `Couldn't resolve code or treatment from: ${sourceTreatment.service_code}`
        );
        return;
      }

      const treatmentConfiguration = await resolveMappTreatmentConfiguration(
        data.defaultTreatmentConfiguration,
        mappedTreatment
      );

      const sourceFeeScheduleId = sourceTreatment.payment_plan_id ?? undefined;
      const feeSchedule = await getPreferredOrDefaultRef(sourceFeeScheduleId);
      const taxStatus = TaxStrategy.GSTFree;
      const price = roundTo2Decimals(sourceTreatment.total_amount);
      const scopeResolver = new ChartedItemScopeResolver();

      const chartedSurfaces = ExactTreatmentPlanBuilder.getChartedSurfaces(
        sourceTreatment,
        practitioner,
        timezone
      ).map((chartedSurface) =>
        determineToothSurfaceByScope(
          chartedSurface,
          treatmentConfiguration,
          scopeResolver
        )
      );

      const serviceCode = code
        ? PricedServiceCodeEntry.init({
            code: code.code,
            type: code.type,
            taxStatus,
            price,
            tax: determineTaxFromStrategy(data.taxRate, {
              taxStatus: taxStatus,
              amount: price,
            }),
            chartedSurfaces,
          })
        : undefined;

      const scopeRefs = scopeResolver.reduceChartedSurfacesToScope(
        treatmentConfiguration,
        chartedSurfaces
      );
      const scopeRef = first(scopeRefs)?.scopeRef ?? {
        scope: ChartableSurface.WholeMouth,
      };

      const resolvedTreatmentData = getResolvedTreatmentData(
        sourceTreatment,
        practitioner,
        timezone
      );

      const serviceCodes = serviceCode ? [serviceCode] : [];
      const basePrice = serviceCodes.length ? 0 : price;

      treatments.push(
        ChartedTreatment.init({
          uuid: sourceTreatment.treatment_id,
          config: toNamedDocument(treatmentConfiguration),
          feeSchedule: toNamedDocument(feeSchedule),
          chartedSurfaces: chartedSurfaces,
          attributedTo: stafferToNamedDoc(practitioner),
          serviceCodes,
          basePrice,
          scopeRef,
          resolvedAt: resolvedTreatmentData?.resolvedAt,
          resolvedBy: resolvedTreatmentData?.resolvedBy,
        })
      );
    });

    return treatments;
  }

  static getChartedSurfaces(
    sourceTreatment: IExactTreatment,
    practitioner: WithRef<IStaffer>,
    timezone: Timezone
  ): IChartedSurface[] {
    if (!sourceTreatment.tooth_range && !sourceTreatment.tooth) {
      return [];
    }

    const teeth =
      sourceTreatment.tooth_range ?? compact([sourceTreatment.tooth]);
    return teeth
      .map((tooth) =>
        getExactChartedRefs(tooth, sourceTreatment.tooth_surfaces).map(
          (chartedRef) => {
            const resolvedTreatmentData = getResolvedTreatmentData(
              sourceTreatment,
              practitioner,
              timezone
            );

            return ChartedSurface.init({
              chartedBy: stafferToNamedDoc(practitioner),
              resolvedAt: resolvedTreatmentData?.resolvedAt,
              resolvedBy: resolvedTreatmentData?.resolvedBy,
              chartedRef,
            });
          }
        )
      )
      .flat();
  }

  private static async _getChartedTreatmentsById(
    planGroups: IGetRecordResponse<ExactTreatment>[][],
    defaultFeeSchedule: INamedDocument<IFeeSchedule>,
    data: IPatientTreatmentPlanJobData<ExactPatient>,
    translationMap: TranslationMapHandler,
    timezone: Timezone
  ): Promise<Map<string, IChartedTreatment>> {
    const chartedTreatments = await resolveSequentially(
      planGroups,
      (planTreatments) =>
        ExactTreatmentPlanBuilder.getChartedTreatments(
          planTreatments.map((treatment) => treatment.data),
          resolveFeeSchedule(translationMap, defaultFeeSchedule),
          data,
          translationMap,
          timezone
        )
    );
    return new Map(
      chartedTreatments
        .flat()
        .map((chartedTreatment): [string, IChartedTreatment] => [
          chartedTreatment.uuid,
          chartedTreatment,
        ])
    );
  }

  private static async _buildStepFromBlueprint(
    blueprint: IExactStepBlueprint,
    chartedTreatmentsById: Map<string, IChartedTreatment>,
    treatmentCategories: WithRef<ITreatmentCategory>[],
    timezone: Timezone
  ): Promise<IStepWithIdentifiers> {
    const createdAt = blueprint.date
      ? toTimestamp(toMomentTz(blueprint.date, timezone))
      : toTimestamp();
    const step = {
      ...TreatmentStep.init({
        name: ExactTreatmentPlanBuilder._getStepName(blueprint, timezone),
        treatments: blueprint.treatments.map((treatment) =>
          ExactTreatmentPlanBuilder._getChartedTreatment(
            treatment,
            chartedTreatmentsById
          )
        ),
        status: ExactTreatmentPlanBuilder._getStepStatus(blueprint),
      }),
      createdAt,
    };

    const treatmentCategory = await CalculateTreatmentWeight.getPrimaryCategory(
      step,
      treatmentCategories
    );

    return {
      ...step,
      display: {
        ...step.display,
        primaryTreatmentCategory: treatmentCategory?.ref,
      },
      sourceIdentifier: blueprint.sourceIdentifier,
      date: toMomentTz(createdAt, timezone).format(ISO_DATE_FORMAT),
    };
  }

  private static _getChartedTreatment(
    treatment: IGetRecordResponse<ExactTreatment>,
    chartedTreatmentsById: Map<string, IChartedTreatment>
  ): IChartedTreatment {
    const chartedTreatment = chartedTreatmentsById.get(
      treatment.data.treatment_id
    );
    if (!chartedTreatment) {
      throw new Error(
        `No charted treatment for ${treatment.data.treatment_id}`
      );
    }
    return chartedTreatment;
  }

  private static _getStepName(
    blueprint: IExactStepBlueprint,
    timezone: Timezone
  ): string {
    const appointment =
      blueprint.boundAppointment ?? blueprint.assignedAppointment;
    if (appointment?.data.treatment_name) {
      return appointment.data.treatment_name;
    }
    if (appointment) {
      const appointmentStatus = upperFirst(appointment.data.appointment_status);
      const appointmentDate = toMomentTz(
        appointment.record.translations.appointmentDateTime,
        timezone
      ).format(HISTORY_DATE_FORMAT);
      return `${appointmentStatus} - ${appointmentDate}`;
    }
    const visitSequence =
      first(blueprint.treatments)?.data.visit_sequence ?? '1';
    return blueprint.date
      ? `Treatment Step ${visitSequence} - ${blueprint.date}`
      : `Treatment Step ${visitSequence}`;
  }

  private static _getStepStatus(
    blueprint: IExactStepBlueprint
  ): TreatmentStepStatus {
    const isCompleted = blueprint.kind === ExactStepKind.Completed;
    if (
      blueprint.boundAppointment &&
      (isCompleted || blueprint.boundByValidatedLink)
    ) {
      return appointmentStatusToTreatmentStepStatus(
        convertExactAppointmentStatus(
          blueprint.boundAppointment.data.appointment_status
        )
      );
    }
    return isCompleted
      ? TreatmentStepStatus.Complete
      : TreatmentStepStatus.Incomplete;
  }
}

function getVisitGroupKey(
  treatment: IGetRecordResponse<ExactTreatment>,
  treatments: IGetRecordResponse<ExactTreatment>[]
): string {
  const lastVisitSequence = determineLastVisitNumber(treatments);
  const visitSequence =
    treatment.data.visit_sequence ?? (lastVisitSequence + 1).toString();
  const date =
    treatment.record.translations.completedDate ??
    treatment.record.translations.plannedDate ??
    'no-date';
  return `${visitSequence}-${date}`;
}
function determineLastVisitNumber(
  treatments: IGetRecordResponse<ExactTreatment>[]
): number {
  return Math.max(
    ...treatments.map((treatment) =>
      treatment.data.visit_sequence ? toInt(treatment.data.visit_sequence) : 0
    )
  );
}

export function convertExactAppointmentStatus(
  status: ExactAppointmentStatus
): AppointmentStatus {
  switch (status) {
    case ExactAppointmentStatus.Booked:
      return AppointmentStatus.Scheduled;
    case ExactAppointmentStatus.Cancelled:
    case ExactAppointmentStatus.Failed:
      return AppointmentStatus.Cancelled;
    case ExactAppointmentStatus.Complete:
      return AppointmentStatus.Complete;
    case ExactAppointmentStatus.Confirmed:
      return AppointmentStatus.Confirmed;
    default:
      return AppointmentStatus.Scheduled;
  }
}

function appointmentStatusToTreatmentStepStatus(
  status: AppointmentStatus
): TreatmentStepStatus {
  switch (status) {
    case AppointmentStatus.Complete:
      return TreatmentStepStatus.Complete;
    default:
      return TreatmentStepStatus.Incomplete;
  }
}
